// Temporary wrapper around FileUploadEditView until we finish migration to Uppy.
// TODO: move FileUploadEditView here, refactor it similar to UrlPart and restyle it.
import * as React from 'react';

import initBem from 'js/lib/bem';

import PlagiarismDetectionContainer, {
  ASSIGNMENT_TYPES,
} from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionContainer';
import { getSimilarityCheckRoleFromAssignmentRole } from 'bundles/academic-integrity/components/plagiarism-detection/utils/SimilarityCheckRoles';
import type { FileUploadSubmissionPartType } from 'bundles/assess-common/types/NaptimeSubmission';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import FileUploadView from 'bundles/compound-assessments/components/form-parts/fileUpload/FileUploadView';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import { typeNames } from 'bundles/compound-assessments/constants';
import type { FileUploadPrompt, FileUploadResponse } from 'bundles/compound-assessments/types/FormParts';
import type { AssignmentRole } from 'bundles/compound-assessments/types/Roles';
import FileUploadEditView from 'bundles/peer/components/FileUploadEditView';

import 'css!./__styles__/FileUpload';

const bem = initBem('FileUpload');

type Props = {
  prompt: FileUploadPrompt;
  response: FileUploadResponse;
  responseId: string;
  // storedResponse is the response stored in the backend
  storedResponse?: FileUploadResponse;
  onChangeResponse: (response: FileUploadResponse) => void;
  isReadOnly: boolean;
  isDisabled: boolean;
  showValidation: boolean;
  itemId: string;
  userId: number;
  teamId?: string;
  courseId: string;
  role?: AssignmentRole;
  renderFileUploadForPreview?: boolean;
};

export const checkInvalid = (response: FileUploadResponse): FormPartsValidationStatus | null =>
  !((((response || {}).definition || {}).submissionPartResponse || {}).definition || {}).fileUrl
    ? FormPartsValidationStatuses.warning
    : null;

type State = {
  hasUnsavedChanges: boolean;
};

class FileUpload extends React.Component<Props, State> {
  state = {
    hasUnsavedChanges: false,
  };

  handleUpdate = (response: FileUploadSubmissionPartType) => {
    const { onChangeResponse } = this.props;
    const newResponse = {
      typeName: typeNames.SUBMISSION_RESPONSE,
      definition: {
        submissionPartResponse: {
          ...response,
          typeName: 'fileUploadResponse',
        },
      },
    };

    this.setState({ hasUnsavedChanges: true });
    // @ts-expect-error TSMIGRATION
    onChangeResponse(newResponse);
  };

  componentDidUpdate(prevProps: Props) {
    /**
     * Reset hasUnsavedChanges when autosaving triggered by file upload url change or manual saving is complete,
     * this will trigger an automatic similarity check if we are using v3.
     * For autosaving triggered by file upload url change, see:
     * @link {https://github.com/webedx-spark/web/blob/0fc4ce2ba85500fe5c1dc5be3d0a5a74ae5dae0c/static/bundles/compound-assessments/components/steps/interactive-step/step-page/cds/SubmitControls.tsx#L106}
     */
    const { isDisabled } = this.props;
    const { hasUnsavedChanges } = this.state;
    if (prevProps.isDisabled && !isDisabled && hasUnsavedChanges) {
      this.setState({ hasUnsavedChanges: false });
    }
  }

  render() {
    const {
      isReadOnly,
      response,
      isDisabled,
      prompt: { id, plagiarismConfig },
      showValidation,
      responseId,
      userId,
      courseId,
      itemId,
      role,
      renderFileUploadForPreview,
      teamId,
    } = this.props;

    const submissionPart = {
      ...response.definition.submissionPartResponse,
      typeName: 'fileUpload',
    };

    const blockSubmit = () => {
      /* Empty function */
    }; // TODO

    const isInvalid = !!(showValidation && checkInvalid(response));

    return (
      <div className={bem(undefined, { isInvalid })}>
        {isReadOnly ? (
          <FileUploadView
            submissionPart={submissionPart.definition}
            renderFileUploadForPreview={renderFileUploadForPreview}
          />
        ) : (
          <FileUploadEditView
            id={id}
            // @ts-expect-error TSMIGRATION
            submissionPart={submissionPart}
            disabled={isDisabled}
            update={this.handleUpdate}
            blockSubmit={blockSubmit}
            uploadButtonHtmlAttributes={{
              'aria-describedby': isInvalid ? `validation-error-${id}` : undefined,
            }}
          />
        )}
        {plagiarismConfig?.plagiarismCheckEnable && (
          <PlagiarismDetectionContainer
            courseId={courseId}
            itemId={itemId}
            authorId={userId}
            assignmentType={ASSIGNMENT_TYPES.STAFF}
            responseId={responseId}
            hasContent={!!submissionPart?.definition?.fileUrl && submissionPart.definition.fileUrl !== ''}
            hasUnsavedChanges={this.state.hasUnsavedChanges}
            role={getSimilarityCheckRoleFromAssignmentRole(role)}
            teamId={teamId}
          />
        )}
        {isInvalid && <ValidationError id={id} />}
      </div>
    );
  }
}

export default FileUpload;
